<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { WizardPage, WizardField } from '@/types/wizard-forms'
import type { FieldAnswer } from '@/types/registration'

const props = defineProps<{
  page: WizardPage
  fieldAnswers: Map<number, FieldAnswer>
  validationErrors: Record<number, string>
}>()

const emit = defineEmits<{
  'update:answer': [wizardFieldId: number, value: string | null, selectedOptions?: number[]]
}>()

const { t } = useI18n()

interface GridCell {
  row: number
  col: number
  field: WizardField
}

const gridCells = computed((): GridCell[] => {
  return (props.page.fields ?? []).map((f) => ({
    row: f.grid_row,
    col: f.grid_column,
    field: f,
  }))
})

const maxRow = computed((): number => {
  if (gridCells.value.length === 0) return 0
  return Math.max(...gridCells.value.map((c) => c.row))
})

const maxCol = computed((): number => {
  if (gridCells.value.length === 0) return 0
  return Math.max(...gridCells.value.map((c) => c.col))
})

function getFieldsAt(row: number, col: number): WizardField[] {
  return gridCells.value
    .filter((c) => c.row === row && c.col === col)
    .map((c) => c.field)
}

function getValue(fieldId: number): string {
  return props.fieldAnswers.get(fieldId)?.value ?? ''
}

function getCheckboxValue(fieldId: number): boolean {
  const val = props.fieldAnswers.get(fieldId)?.value
  return val === '1'
}

function getSelectedOptions(fieldId: number): number[] {
  return props.fieldAnswers.get(fieldId)?.selected_options ?? []
}

function onTextInput(fieldId: number, value: string): void {
  emit('update:answer', fieldId, value || null)
}

function onCheckboxChange(fieldId: number, checked: boolean): void {
  emit('update:answer', fieldId, checked ? '1' : '0')
}

function onOptionGroupChange(
  fieldId: number,
  optionId: number,
  optionValue: string,
  checked: boolean,
  isCheckboxGroup: boolean,
): void {
  if (isCheckboxGroup) {
    const current = getSelectedOptions(fieldId)
    const updated = checked
      ? [...current, optionId]
      : current.filter((id) => id !== optionId)
    const values = (props.page.fields ?? [])
      .find((f) => f.id === fieldId)
      ?.options?.filter((o) => updated.includes(o.id))
      .map((o) => o.value)
      .join(',') ?? ''
    emit('update:answer', fieldId, values || null, updated)
  } else {
    // radio group
    emit('update:answer', fieldId, optionValue, [optionId])
  }
}

function onSelectChange(
  fieldId: number,
  selectedOptionValue: string,
  options: { id: number; value: string }[],
): void {
  const option = options.find((o) => o.value === selectedOptionValue)
  const selectedIds = option ? [option.id] : []
  emit('update:answer', fieldId, selectedOptionValue || null, selectedIds)
}
</script>

<template>
  <div>
    <h2 class="text-xl font-bold text-slate-900 mb-5">{{ page.title }}</h2>

    <div
      v-for="row in maxRow"
      :key="row"
      class="grid gap-4 mb-4"
      :style="{ gridTemplateColumns: `repeat(${maxCol}, minmax(0, 1fr))` }"
    >
      <div
        v-for="col in maxCol"
        :key="col"
      >
        <template v-for="field in getFieldsAt(row, col)" :key="field.id">
          <div class="space-y-1">
            <label
              :for="`field-${field.id}`"
              class="block text-sm font-medium text-slate-700"
            >
              {{ field.label }}
              <span v-if="field.is_required" class="text-red-500 ml-0.5">*</span>
            </label>

            <!-- text -->
            <input
              v-if="field.type === 'text'"
              :id="`field-${field.id}`"
              type="text"
              :value="getValue(field.id)"
              :placeholder="field.placeholder ?? ''"
              class="w-full rounded-lg border px-4 py-2.5 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
              :class="validationErrors[field.id] ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white focus:border-emerald-500'"
              @input="onTextInput(field.id, ($event.target as HTMLInputElement).value)"
            />

            <!-- textarea -->
            <textarea
              v-else-if="field.type === 'textarea'"
              :id="`field-${field.id}`"
              :value="getValue(field.id)"
              :placeholder="field.placeholder ?? ''"
              rows="3"
              class="w-full rounded-lg border px-4 py-2.5 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors resize-none"
              :class="validationErrors[field.id] ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white focus:border-emerald-500'"
              @input="onTextInput(field.id, ($event.target as HTMLTextAreaElement).value)"
            />

            <!-- number -->
            <input
              v-else-if="field.type === 'number'"
              :id="`field-${field.id}`"
              type="number"
              :value="getValue(field.id)"
              :placeholder="field.placeholder ?? ''"
              class="w-full rounded-lg border px-4 py-2.5 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
              :class="validationErrors[field.id] ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white focus:border-emerald-500'"
              @input="onTextInput(field.id, ($event.target as HTMLInputElement).value)"
            />

            <!-- date_picker -->
            <input
              v-else-if="field.type === 'date_picker'"
              :id="`field-${field.id}`"
              type="date"
              :value="getValue(field.id)"
              class="w-full rounded-lg border px-4 py-2.5 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
              :class="validationErrors[field.id] ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white focus:border-emerald-500'"
              @input="onTextInput(field.id, ($event.target as HTMLInputElement).value)"
            />

            <!-- checkbox (single boolean) -->
            <div v-else-if="field.type === 'checkbox'" class="flex items-center gap-2">
              <input
                :id="`field-${field.id}`"
                type="checkbox"
                :checked="getCheckboxValue(field.id)"
                class="w-4 h-4 rounded accent-emerald-500 cursor-pointer"
                @change="onCheckboxChange(field.id, ($event.target as HTMLInputElement).checked)"
              />
              <label :for="`field-${field.id}`" class="text-sm text-slate-600 cursor-pointer">
                {{ field.placeholder ?? field.label }}
                <span
                  v-if="field.extra_cost !== null && parseFloat(field.extra_cost) > 0"
                  class="text-emerald-600 text-xs ml-1"
                >
                  + EUR {{ parseFloat(field.extra_cost).toFixed(2) }}
                </span>
              </label>
            </div>

            <!-- checkbox_group -->
            <div v-else-if="field.type === 'checkbox_group'" class="space-y-2">
              <div
                v-for="option in field.options ?? []"
                :key="option.id"
                class="flex items-center gap-2"
              >
                <input
                  :id="`field-${field.id}-opt-${option.id}`"
                  type="checkbox"
                  :value="option.id"
                  :checked="getSelectedOptions(field.id).includes(option.id)"
                  class="w-4 h-4 rounded accent-emerald-500 cursor-pointer"
                  @change="onOptionGroupChange(
                    field.id,
                    option.id,
                    option.value,
                    ($event.target as HTMLInputElement).checked,
                    true,
                  )"
                />
                <label
                  :for="`field-${field.id}-opt-${option.id}`"
                  class="text-sm text-slate-700 cursor-pointer"
                >
                  {{ option.label }}
                  <span
                    v-if="parseFloat(option.extra_cost) > 0"
                    class="text-emerald-600 text-xs ml-1"
                  >
                    + EUR {{ parseFloat(option.extra_cost).toFixed(2) }}
                  </span>
                </label>
              </div>
            </div>

            <!-- radio_group -->
            <div v-else-if="field.type === 'radio_group'" class="space-y-2">
              <div
                v-for="option in field.options ?? []"
                :key="option.id"
                class="flex items-center gap-2"
              >
                <input
                  :id="`field-${field.id}-opt-${option.id}`"
                  type="radio"
                  :name="`field-${field.id}`"
                  :value="option.id"
                  :checked="getSelectedOptions(field.id).includes(option.id)"
                  class="w-4 h-4 accent-emerald-500 cursor-pointer"
                  @change="onOptionGroupChange(
                    field.id,
                    option.id,
                    option.value,
                    true,
                    false,
                  )"
                />
                <label
                  :for="`field-${field.id}-opt-${option.id}`"
                  class="text-sm text-slate-700 cursor-pointer"
                >
                  {{ option.label }}
                  <span
                    v-if="parseFloat(option.extra_cost) > 0"
                    class="text-emerald-600 text-xs ml-1"
                  >
                    + EUR {{ parseFloat(option.extra_cost).toFixed(2) }}
                  </span>
                </label>
              </div>
            </div>

            <!-- select -->
            <select
              v-else-if="field.type === 'select'"
              :id="`field-${field.id}`"
              :value="getValue(field.id)"
              class="w-full rounded-lg border px-4 py-2.5 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
              :class="validationErrors[field.id] ? 'border-red-400 bg-red-50' : 'border-slate-300 bg-white focus:border-emerald-500'"
              @change="onSelectChange(
                field.id,
                ($event.target as HTMLSelectElement).value,
                field.options ?? [],
              )"
            >
              <option value="">{{ field.placeholder ?? '-----' }}</option>
              <option
                v-for="option in field.options ?? []"
                :key="option.id"
                :value="option.value"
              >
                {{ option.label }}
                <template v-if="parseFloat(option.extra_cost) > 0">
                  (+ EUR {{ parseFloat(option.extra_cost).toFixed(2) }})
                </template>
              </option>
            </select>

            <!-- help text -->
            <p v-if="field.help_text" class="text-xs text-slate-500">{{ field.help_text }}</p>

            <!-- validation error -->
            <p v-if="validationErrors[field.id]" class="text-xs text-red-500">
              {{ validationErrors[field.id] }}
            </p>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
